/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
'use client';

import 'client-only';
import { useFocusable } from 'react-aria/useFocusable';
import { mergeProps } from 'react-aria/mergeProps';
import { useObjectRef } from 'react-aria/useObjectRef';
import { mergeRefs } from 'react-aria/mergeRefs';
import {
  Children,
  cloneElement,
  type DOMAttributes,
  type ReactElement,
  type ReactNode,
  version,
} from 'react';
import { TooltipTrigger as AriaTooltipTrigger } from 'react-aria-components/Tooltip';
import { useContextProps } from 'react-aria-components/slots';
import { TooltipContext } from './context';
import type { FocusableElement } from '@react-types/shared';
import type { TooltipFocusableProps, TooltipTriggerProps } from './types';

function TooltipFocusable({ children, ref, ...props }: TooltipFocusableProps) {
  ref = useObjectRef(ref);

  const { focusableProps } = useFocusable(props, ref);
  const [trigger, tooltip] = Children.toArray(children) as [
    ReactElement<DOMAttributes<FocusableElement>, string>,
    ReactNode,
  ];

  const childRef =
    //@ts-expect-error
    Number.parseInt(version, 10) < 19 ? trigger.ref : trigger.props.ref;

  return (
    <>
      {cloneElement(trigger, {
        ...mergeProps(focusableProps, trigger.props),
        //@ts-expect-error
        ref: mergeRefs(childRef, ref),
      })}
      {tooltip}
    </>
  );
}

/**
 * TooltipTrigger - Wrapper that manages tooltip display on hover or focus
 *
 * @example
 * ```tsx
 * <TooltipTrigger delay={500}>
 *   <Button>
 *     <Icon><Info /></Icon>
 *   </Button>
 *   <Tooltip>Additional information about this action</Tooltip>
 * </TooltipTrigger>
 * ```
 *
 * @param props - {@link TooltipTriggerProps}
 * @param props.ref - Ref to the focusable trigger element.
 * @param props.children - Trigger element and Tooltip content.
 * @param props.delay - Delay in milliseconds before showing the tooltip.
 * @returns The rendered TooltipTrigger component.
 */
export function TooltipTrigger({ ref, ...props }: TooltipTriggerProps) {
  [props, ref] = useContextProps(props, ref ?? null, TooltipContext);

  const { children, delay = 250, ...rest } = props;

  return (
    <AriaTooltipTrigger {...rest} delay={delay}>
      <TooltipFocusable ref={ref}>{children}</TooltipFocusable>
    </AriaTooltipTrigger>
  );
}
